import React, { Component } from 'react';
import { Modal, Form, Grid, Button, Icon, Header } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { AgGridReact } from '@ag-grid-community/react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { v4 as uuid } from 'uuid';
import {
  DEFAULT_AUTOMATE_DAILY_RUN_AREA_SETTINGS,
  DEFAULT_AUTOMATE_DAILY_RUN_SCHEDULE_ROW_SEETINGS,
  DEFAULT_RUN_ENGINE_SETTINGS,
  DAYS_OF_WEEK,
  AUTOMATE_DAILY_RUN_TABLE_COLUMNS,
  PUBLISH_AS_PLANNED_OPTIONS,
  DEFAULT_PUBLISH_AS_PLANNED_OPTION_ID,
  GRIDS_DATE_DISPLAY_FORMAT,
  MIN_SCHEDULE_FREQUENCY,
  MODAL_CHECK_BOX_LABEL_STYLE,
  MODAL_CHECK_BOX_STYLE,
} from '../../../HelperMethods/constants';
import ActionsColumnRenderer from '../../common/ActionsColumnRenderer';
import EngineParameters from '../TaskViewPage/EngineParameters';
import EngineScheduler from '../TaskViewPage/EngineScheduler';
import { getPickListOptions, capitalizeFirstLetter } from '../../../HelperMethods/UtilityFunctions';
import { GRID_OPTIONS } from '../../../HelperMethods/AgGridConstants';

class AutomateDailyRunModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      schedule: [],
      scheduleRowsToDisplay: [],
      areasErrorMessage: false,
      addScheduleButtonDisabled: false,
      showScheduleParamsView: false,
      scheduleParams: null,
      selectedScheduleId: null,
      errors: {},
    };
    this.daysOfWeekById = _.keyBy(DAYS_OF_WEEK, 'id');
    this.areaDefaultEngineSettings = null;
    this.selectedArea = null;
  }

  componentDidMount() {
    const { settings, selectedAreas } = this.props;
    if (selectedAreas.length !== 1) {
      this.setState({
        areasErrorMessage: true,
      });
      return;
    }
    const { automateDailyRunSettings = {}, runEngineSettings = {} } = settings;
    // eslint-disable-next-line prefer-destructuring
    this.selectedArea = selectedAreas[0];
    const { id } = this.selectedArea;
    const { schedule } = automateDailyRunSettings[id]
      ? _.cloneDeep(automateDailyRunSettings[id])
      : _.cloneDeep(DEFAULT_AUTOMATE_DAILY_RUN_AREA_SETTINGS);
    this.areaDefaultEngineSettings = runEngineSettings[id];
    this.loadScheduleRowsGrid(schedule);
  }

  getAllSelectedDays = (schedule) => {
    return schedule.flatMap((item) => item.selectedDays);
  };

  getScheduleRowsToDisplay = (schedule) => {
    return schedule.map((item) => {
      const {
        name,
        enabled,
        modifiedOn,
        modifiedBy,
        selectedDays,
        runOnFixedTime,
        fixedTimeList,
        frequencyHour,
        frequencyMinutes,
        frequencyStartTime,
        frequencyEndTime,
        publishAsPlanned = DEFAULT_PUBLISH_AS_PLANNED_OPTION_ID,
      } = item;
      return {
        name: capitalizeFirstLetter(name),
        status: enabled ? 'Active' : 'Inactive',
        modifiedOn: moment(modifiedOn).format(GRIDS_DATE_DISPLAY_FORMAT),
        modifiedBy,
        selectedDaysDesc: selectedDays.map((id) => this.daysOfWeekById[id].shortName).join(', '),
        publishAsPlanned: PUBLISH_AS_PLANNED_OPTIONS.filter((option) => option.id === publishAsPlanned)[0].name,
        frequency: runOnFixedTime
          ? fixedTimeList.join(', ')
          : `Every ${frequencyHour} hour ${
              frequencyMinutes && parseInt(frequencyMinutes) ? `${frequencyMinutes} minutes` : ''
            } between ${frequencyStartTime} and ${frequencyEndTime}`,
        item,
      };
    });
  };

  loadScheduleRowsGrid = (schedule) => {
    const scheduleRowsToDisplay = this.getScheduleRowsToDisplay(schedule);
    const addScheduleButtonDisabled = this.getAllSelectedDays(schedule).length === DAYS_OF_WEEK.length;
    this.setState({
      schedule,
      scheduleRowsToDisplay,
      addScheduleButtonDisabled,
      showScheduleParamsView: false,
      selectedScheduleId: null,
      errors: {},
    });
  };

  addSelectedDaysDropDownToScheduleParams = (scheduleParams) => {
    const availableDaysIds = _.difference(
      DAYS_OF_WEEK.map((item) => item.id),
      this.getAllSelectedDays(this.state.schedule)
    );
    scheduleParams.selectedDaysDropDown = getPickListOptions(
      [...availableDaysIds, ...scheduleParams.selectedDays].map((id) => this.daysOfWeekById[id])
    );
  };

  addSchedule = () => {
    const scheduleParams = _.cloneDeep(DEFAULT_AUTOMATE_DAILY_RUN_SCHEDULE_ROW_SEETINGS);
    scheduleParams.id = uuid();
    this.addSelectedDaysDropDownToScheduleParams(scheduleParams);
    scheduleParams.engineSettings = this.areaDefaultEngineSettings
      ? _.cloneDeep(this.areaDefaultEngineSettings)
      : _.cloneDeep(DEFAULT_RUN_ENGINE_SETTINGS);
    this.setState({
      scheduleParams,
      showScheduleParamsView: true,
    });
  };

  onScheduleParamsInputChange = (field, value) => {
    this.setState((state) => {
      return {
        scheduleParams: Object.assign(state.scheduleParams, { [field]: value }),
      };
    });
  };

  onScheduleParamsEngineSettingsInputChange = (field, value) => {
    this.setState((state) => {
      return {
        scheduleParams: Object.assign(
          state.scheduleParams,
          Object.assign(state.scheduleParams.engineSettings, { [field]: value })
        ),
      };
    });
  };

  onSaveSchedule = () => {
    const newScheduleObj = {};
    for (const key of Object.keys(DEFAULT_AUTOMATE_DAILY_RUN_SCHEDULE_ROW_SEETINGS)) {
      newScheduleObj[key] = this.state.scheduleParams[key];
    }
    newScheduleObj.modifiedOn = moment().toISOString();
    newScheduleObj.modifiedBy = this.props.loggedInUser.userName;
    newScheduleObj.engineSettings = this.props.evaluateEngineSettings(newScheduleObj.engineSettings);
    const isValidObject = this.validateScheduleObj(newScheduleObj);
    if (!isValidObject) {
      return;
    }
    let schedule;
    if (!this.state.selectedScheduleId) {
      schedule = [...this.state.schedule, newScheduleObj];
    } else {
      const scheduleIndex = _.findIndex(this.state.schedule, (o) => o.id === newScheduleObj.id);
      schedule = [...this.state.schedule];
      schedule.splice(scheduleIndex, 1, newScheduleObj);
    }
    this.loadScheduleRowsGrid(schedule);
    this.onSaveSettings(schedule);
  };

  onCancelSchedule = () => {
    this.setState({
      showScheduleParamsView: false,
      errors: {},
    });
  };

  deleteScheduleRow = (data) => {
    if (!data) {
      return;
    }
    const { id } = data.item;
    const scheduleIndex = _.findIndex(this.state.schedule, (o) => o.id === id);
    const schedule = [...this.state.schedule];
    schedule.splice(scheduleIndex, 1);
    this.loadScheduleRowsGrid(schedule);
    this.onSaveSettings(schedule);
  };

  editScheduleRow = (data) => {
    if (!data) {
      return;
    }
    const scheduleParams = data.item;
    this.addSelectedDaysDropDownToScheduleParams(scheduleParams);
    // if (scheduleParams.useDefaultEngineSettings && this.areaDefaultEngineSettings) {
    //   scheduleParams.engineSettings = _.cloneDeep(this.areaDefaultEngineSettings);
    // }
    this.setState({
      showScheduleParamsView: true,
      scheduleParams,
      selectedScheduleId: scheduleParams.id,
    });
  };

  onSaveSettings = (schedule) => {
    const areaNewSettings = _.cloneDeep(DEFAULT_AUTOMATE_DAILY_RUN_AREA_SETTINGS);
    areaNewSettings.schedule = schedule;
    this.props.saveAutomateDailyRunSettings(areaNewSettings, this.selectedArea.id);
  };

  getHeadertext = () => {
    const areaNameText = this.selectedArea ? `- ${capitalizeFirstLetter(this.selectedArea.name)} Area` : '';
    let preText;
    if (this.state.showScheduleParamsView) {
      preText = this.state.selectedScheduleId
        ? `Edit "${this.state.scheduleParams.name}" Schedule`
        : 'Add New Schedule';
    } else {
      preText = `Schedules List`;
    }
    return `${preText} ${areaNameText}`;
  };

  validateMinutesStep = (minutes) => {
    return parseInt(minutes, 10) % MIN_SCHEDULE_FREQUENCY === 0;
  };

  validateScheduleObj = (scheduleObj) => {
    const errors = {};
    const {
      name,
      selectedDays,
      timeZone,
      runOnFixedTime,
      frequencyHour,
      frequencyMinutes,
      frequencyStartTime,
      frequencyEndTime,
      fixedTimeList,
      publishAsPlanned,
    } = scheduleObj;

    const minStepError = `${MIN_SCHEDULE_FREQUENCY} mins is minimum step`;

    if (!name) {
      errors.name = 'Schedule name is mandatory';
    }
    if (selectedDays && selectedDays.length === 0) {
      errors.selectedDays = 'Days are mandatory';
    }
    if (!timeZone) {
      errors.timeZone = 'TimeZone is mandatory';
    }
    if (!publishAsPlanned) {
      errors.publishAsPlanned = 'Publish as Planned option is mandatory';
    }
    if (runOnFixedTime) {
      if (fixedTimeList.length) {
        for (const item of fixedTimeList) {
          const minutes = item.split(':')[1];
          if (!this.validateMinutesStep(minutes)) {
            errors.fixedTimeList = minStepError;
          }
        }
      } else {
        errors.fixedTimeList = 'Fixed Timings are not added';
      }
    }
    if (!runOnFixedTime) {
      if (frequencyHour === '') {
        errors.frequencyHour = 'Hour is mandatory';
      }
      if (frequencyMinutes === '') {
        errors.frequencyMinutes = 'Minutes is mandatory';
      }
      if (!frequencyHour && !frequencyMinutes) {
        errors.frequencyHour = 'Hour and Minutes can not be zero';
      }
      if (!frequencyStartTime || frequencyStartTime === '00:00') {
        errors.frequencyStartTime = 'Start time is mandatory';
      }
      if (!frequencyEndTime || frequencyEndTime === '00:00') {
        errors.frequencyEndTime = 'End time is mandatory';
      }

      // all times must have step of MIN_SCHEDULE_FREQUENCY only.
      if (!this.validateMinutesStep(frequencyMinutes)) {
        errors.frequencyMinutes = minStepError;
      }

      if (frequencyEndTime && frequencyStartTime) {
        const [startTimeHours, startTimeMinutes] = frequencyStartTime.split(':');
        const [endTimeHours, endTimeMinutes] = frequencyEndTime.split(':');
        const momentStartTime = moment({ hours: startTimeHours, minutes: startTimeMinutes });
        const momentEndTime = moment({ hours: endTimeHours, minutes: endTimeMinutes });
        // end Time should be greater than start time
        if (momentEndTime.diff(momentStartTime) < 0) {
          errors.frequencyEndTime = 'End time less than start time';
        }

        if (!this.validateMinutesStep(momentStartTime.minutes())) {
          errors.frequencyStartTime = minStepError;
        }

        if (!this.validateMinutesStep(momentEndTime.minutes())) {
          errors.frequencyEndTime = minStepError;
        }
      }
    }

    this.setState({ errors });
    return _.isEmpty(errors);
  };

  updateError = (key, message) => {
    this.setState((state) => {
      return {
        errors: { ...state.errors, [key]: message },
      };
    });
  };

  render() {
    const { errors, areasErrorMessage, showScheduleParamsView, addScheduleButtonDisabled, scheduleParams } = this.state;
    return (
      <div>
        <Modal
          className="custom-settings-modal automate-run-settings-modal"
          size={this.props.size}
          open={this.props.open}
          onClose={() => {
            this.props.close(this.props.toggleModalStateKey, false);
          }}
          centered
        >
          <Modal.Header>{this.getHeadertext()}</Modal.Header>
          <Modal.Content scrolling>
            {areasErrorMessage && (
              <Grid>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    {this.props.selectedAreas.length === 0
                      ? 'Please select an area to configure.'
                      : 'Please select only one area to configure.'}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
            {!areasErrorMessage && !showScheduleParamsView && (
              <Grid>
                {!addScheduleButtonDisabled && (
                  <Grid.Row columns={1}>
                    <Grid.Column width={4} floated="right">
                      <Button
                        primary
                        size="tiny"
                        disabled={addScheduleButtonDisabled}
                        type="button"
                        floated="right"
                        onClick={() => {
                          this.addSchedule();
                        }}
                      >
                        <Icon name="plus" />
                        Add a schedule
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                )}
                {
                  <Grid.Row columns={1}>
                    <Grid.Column className="ag-theme-balham" style={{ height: '300px' }}>
                      <AgGridReact
                        {...GRID_OPTIONS}
                        columnDefs={AUTOMATE_DAILY_RUN_TABLE_COLUMNS}
                        rowData={this.state.scheduleRowsToDisplay}
                        onRowDataChanged={(params) => {
                          params.columnApi.autoSizeAllColumns();
                        }}
                        frameworkComponents={{
                          actionsColumnRenderer: ActionsColumnRenderer,
                        }}
                        context={{
                          deleteItem: this.deleteScheduleRow,
                          editItem: this.editScheduleRow,
                          disableDeleteConfirmationModal: true,
                        }}
                      />
                    </Grid.Column>
                  </Grid.Row>
                }
              </Grid>
            )}
            {!areasErrorMessage && showScheduleParamsView && (
              <Form autoComplete="off">
                <Grid>
                  <Grid.Row columns={3}>
                    <Grid.Column width={5}>
                      <EngineScheduler
                        scheduleParams={scheduleParams}
                        onScheduleParamsInputChange={this.onScheduleParamsEngineSettingsInputChange}
                        errors={errors}
                        updateError={this.updateError}
                        validateMinutesStep={this.validateMinutesStep}
                      />
                    </Grid.Column>
                    <Grid.Column width={1} />
                    <Grid.Column width={10}>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column>
                            <Header size="small">Engine Settings</Header>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column>
                            <Form.Field
                              inline
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                this.onScheduleParamsInputChange(
                                  'useDefaultEngineSettings',
                                  !scheduleParams.useDefaultEngineSettings
                                );
                              }}
                            >
                              {scheduleParams.useDefaultEngineSettings ? (
                                <i style={MODAL_CHECK_BOX_STYLE} className="fa fa-check-square ag-grid-checkbox" />
                              ) : (
                                <i style={MODAL_CHECK_BOX_STYLE} className="fa fa-square ag-grid-checkbox" />
                              )}
                              <label style={MODAL_CHECK_BOX_LABEL_STYLE}>Use Default Engine Settings</label>
                            </Form.Field>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column>
                            <EngineParameters
                              onEngineParameterChange={this.onScheduleParamsEngineSettingsInputChange}
                              parameters={scheduleParams.engineSettings}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Grid>
              <Grid.Row className="form-button-row settings-footer" style={{ marginTop: '10px' }}>
                {!showScheduleParamsView && (
                  <Grid.Column width={16}>
                    <Button
                      basic
                      floated="right"
                      type="button"
                      onClick={() => {
                        this.props.close(this.props.toggleModalStateKey, false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid.Column>
                )}
                {showScheduleParamsView && (
                  <Grid.Column>
                    <Button primary floated="right" type="button" onClick={() => this.onSaveSchedule()}>
                      Save Schedule
                    </Button>
                    <Button basic floated="right" type="button" onClick={() => this.onCancelSchedule()}>
                      Back to List
                    </Button>
                  </Grid.Column>
                )}
              </Grid.Row>
            </Grid>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { settings, selectedAreas, loggedInUser } = state.appReducer;
  return {
    settings,
    selectedAreas,
    loggedInUser,
  };
}
export default connect(mapStateToProps, null)(AutomateDailyRunModal);
